/**
 * The game's saved state in localStorage. Key names and value formats are the
 * ones the single-file site used, so players keep their best height. Every
 * access survives a throwing localStorage (Safari private mode, blocked site
 * data): reads fall back to the defaults and writes are dropped.
 */

const BEST = "stacker-best";
// The panel used to start shown, and the game saved that on every start, so
// the old "stacker-debug" key holds "1" for nearly every returning player. A
// new key lets the panel start hidden for them too.
const DEBUG = "stacker-debug-v2";
const HELP_SEEN = "stacker-help-seen";

/** `undefined` when storage throws, so callers can tell "blocked" from "missing". */
function tryGet(key: string): string | null | undefined {
  try {
    return localStorage.getItem(key);
  } catch {
    return undefined;
  }
}

function trySet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // blocked or full: the value just isn't kept
  }
}

/** The best tower height in metres, 0 when none is saved. */
export function loadBest(): number {
  return parseFloat(tryGet(BEST) ?? "") || 0;
}

export function saveBest(best: number): void {
  trySet(BEST, best.toFixed(2));
}

/** Whether the debug panel is shown; off unless the player turned it on. */
export function loadDebugOn(): boolean {
  return tryGet(DEBUG) === "1";
}

export function saveDebugOn(on: boolean): void {
  trySet(DEBUG, on ? "1" : "0");
}

/**
 * True on the player's very first visit, and marks the help as seen. False
 * when storage can't even be read. When only saving fails, it stays true on
 * every visit, as on the single-file site.
 */
export function takeFirstVisit(): boolean {
  if (tryGet(HELP_SEEN) !== null) return false;
  trySet(HELP_SEEN, "1");
  return true;
}
